import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { userService } from '../api/userService';
import ContentLoader from '../components/ContentLoader';
import { X, User, Briefcase, Calendar } from 'lucide-react';

const CoordinatorTeams = () => {
    const { user: authUser } = useAuth();
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Get all coordinator divisions for this user
    const myDivisions = useMemo(() => {
        const roles = authUser?.roles || [];
        const divs = roles
            .filter(r => r.startsWith('COORDINATOR_'))
            .map(r => r.split('_').slice(1).join('_').toUpperCase());
        console.log('[CoordTeams] My Divisions:', divs);
        return divs;
    }, [authUser]);

    const { data: usersData = [], isLoading: loadingUsers, error: usersError } = useQuery({
        queryKey: ['all_users'],
        queryFn: async () => {
            const data = await userService.getAllUsers();
            const list = Array.isArray(data) ? data : (data?.Users || []);
            return list;
        },
        staleTime: 300000,
    });

    const selectedUserId = selectedUser ? (selectedUser.UserId || selectedUser.Username || selectedUser.sub || selectedUser.id) : null;

    const { data: userDetails, isLoading: loadingDetails, error: detailsError } = useQuery({
        queryKey: ['user_details', selectedUserId],
        queryFn: () => userService.getUserDetail(selectedUserId),
        enabled: !!selectedUserId,
        staleTime: 60000
    });

    const filteredUsers = useMemo(() => {
        const term = searchTerm.toLowerCase();
        return usersData.filter(u => {
            const divAttribute = u.Attributes?.find(a => a.Name === 'custom:division')?.Value;
            const div = (u.Division || divAttribute || '').toUpperCase();

            // Filter by coordinator's divisions
            const isMyTeam = myDivisions.some(md => div === md.toUpperCase() || div.includes(md.toUpperCase()));
            if (!isMyTeam) return false;

            const name = (u.Name || u.Username || '').toLowerCase();
            const email = (u.Email || '').toLowerCase();
            const group = div.toLowerCase();

            return name.includes(term) || email.includes(term) || group.includes(term);
        });
    }, [usersData, myDivisions, searchTerm]);

    const handleUserClick = (u) => {
        setSelectedUser(u);
    };

    const closeModal = () => {
        setSelectedUser(null);
    };

    if (loadingUsers) return <ContentLoader text="Loading Team..." />;

    if (usersError) return (
        <div style={{ padding: '2rem', color: '#ef4444', textAlign: 'center' }}>
            <p>Failed to load members. Please ensure the API is available.</p>
        </div>
    );

    if (!myDivisions.length) {
        return (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                <User size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                <p>You are not assigned as a Coordinator for any division.</p>
            </div>
        );
    }

    return (
        <div style={{ height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', padding: '0.75rem' }}>
            <div style={{ marginBottom: '0.5rem' }}>
                <h2 style={{ margin: '0 0 0.25rem 0', fontSize: '1.25rem' }}>Team Member History</h2>
                <p style={{ margin: 0, color: '#666', fontSize: '0.85rem' }}>View members and history for {myDivisions.join(' & ')}.</p>
            </div>

            <div style={{ flex: 1, display: 'flex', gap: '0.75rem', overflow: 'hidden' }}>
                {/* Left Panel: User List */}
                <div className="modern-card" style={{
                    flex: '0 0 280px', display: 'flex', flexDirection: 'column',
                    overflow: 'hidden', padding: 0
                }}>
                    <div style={{ padding: '0.75rem', borderBottom: '1px solid #eee', background: '#f8f9fa' }}>
                        <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>Members ({filteredUsers.length})</h4>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="modern-input"
                            style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.85rem', padding: '0.4rem' }}
                        />
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', padding: '0.25rem' }}>
                        {filteredUsers.map((u, index) => {
                            const isSelected = selectedUser === u;
                            return (
                                <div
                                    key={index}
                                    onClick={() => handleUserClick(u)}
                                    style={{
                                        padding: '0.5rem', borderBottom: '1px solid #f0f0f0', cursor: 'pointer',
                                        background: isSelected ? '#e3f2fd' : 'transparent',
                                        borderRadius: '4px', marginBottom: '0.1rem',
                                        transition: 'background 0.2s'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <div style={{
                                            width: '32px', height: '32px', borderRadius: '50%', overflow: 'hidden',
                                            background: u.Attributes?.find(a => a.Name === 'picture')?.Value ? 'transparent' : '#e9ecef',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                                        }}>
                                            {u.Attributes?.find(a => a.Name === 'picture')?.Value ? (
                                                <img
                                                    src={u.Attributes.find(a => a.Name === 'picture').Value}
                                                    alt={u.Name || u.Username}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <User size={14} color="#495057" />
                                            )}
                                        </div>
                                        <div style={{ overflow: 'hidden', flex: 1 }}>
                                            <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#2c3e50', marginBottom: '0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {u.Name || u.Username}
                                            </div>
                                            <div style={{ fontSize: '0.75rem', color: '#555', marginBottom: '0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {u.Email || 'No Email'}
                                            </div>
                                            <div style={{ fontSize: '0.7rem', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {u.Profile && <span style={{ marginRight: '6px', color: '#4a90e2' }}>{u.Profile}</span>}
                                                <span style={{ color: '#aaa' }}>|</span>
                                                <span style={{ marginLeft: '6px' }}>{u.Division || u.Attributes?.find(a => a.Name === 'custom:division')?.Value}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Right Panel: Details */}
                <div className="modern-card" style={{
                    flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0
                }}>
                    {selectedUser ? (
                        <>
                            <div style={{
                                padding: '1rem',
                                background: 'linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)',
                                color: 'white',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <div style={{
                                        width: '48px', height: '48px', borderRadius: '50%', overflow: 'hidden',
                                        background: selectedUser.Attributes?.find(a => a.Name === 'picture')?.Value ? 'transparent' : 'rgba(255,255,255,0.2)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                    }}>
                                        {selectedUser.Attributes?.find(a => a.Name === 'picture')?.Value ? (
                                            <img
                                                src={selectedUser.Attributes.find(a => a.Name === 'picture').Value}
                                                alt={selectedUser.Name || selectedUser.Username}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <User size={24} color="white" />
                                        )}
                                    </div>
                                    <div>
                                        <h3 style={{ margin: '0 0 0.1rem 0', fontSize: '1.25rem', fontWeight: 'bold', color: 'white', display: 'flex', alignItems: 'center' }}>
                                            {selectedUser.Name || selectedUser.Username}
                                            {userDetails?.HighestQualification && <span style={{ marginLeft: '12px', background: '#3b82f6', color: 'white', fontSize: '0.8rem', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>{userDetails.HighestQualification}</span>}
                                        </h3>

                                        {/* Skill Tags Mirroring DG UI */}
                                        {userDetails && (userDetails.ThematicSpecialization?.length > 0 || userDetails.AreaSpecialization?.length > 0 || userDetails.SpecialSkills?.length > 0) ? (
                                            <table style={{ marginTop: '0.75rem', borderCollapse: 'collapse' }}>
                                                <tbody>
                                                    <tr>
                                                        {(userDetails.ThematicSpecialization?.length > 0 || userDetails.Tags?.length > 0) && (
                                                            <td style={{ verticalAlign: 'top', paddingRight: '1.5rem', borderRight: '1px solid rgba(255,255,255,0.1)' }}>
                                                                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#93c5fd', textTransform: 'uppercase', marginBottom: '0.4rem', letterSpacing: '0.05em' }}>Thematic</div>
                                                                <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', maxWidth: '180px' }}>
                                                                    {(userDetails.ThematicSpecialization?.length > 0 ? userDetails.ThematicSpecialization : (userDetails.Tags || [])).map((tag, i) => (
                                                                        <span key={i} style={{ background: '#e0f2fe', color: '#0369a1', padding: '1px 6px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: 500 }}>{tag}</span>
                                                                    ))}
                                                                </div>
                                                            </td>
                                                        )}
                                                        {userDetails.AreaSpecialization?.length > 0 && (
                                                            <td style={{ verticalAlign: 'top', paddingRight: '1.5rem', paddingLeft: '1.5rem', borderRight: userDetails.SpecialSkills?.length > 0 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                                                                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6ee7b7', textTransform: 'uppercase', marginBottom: '0.4rem', letterSpacing: '0.05em' }}>Area</div>
                                                                <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', maxWidth: '180px' }}>
                                                                    {userDetails.AreaSpecialization.map((tag, i) => <span key={i} style={{ background: '#ecfdf5', color: '#047857', padding: '1px 6px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: 500 }}>{tag}</span>)}
                                                                </div>
                                                            </td>
                                                        )}
                                                        {userDetails.SpecialSkills?.length > 0 && (
                                                            <td style={{ verticalAlign: 'top', paddingLeft: '1.5rem' }}>
                                                                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#fcd34d', textTransform: 'uppercase', marginBottom: '0.4rem', letterSpacing: '0.05em' }}>Special</div>
                                                                <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', maxWidth: '180px' }}>
                                                                    {userDetails.SpecialSkills.map((tag, i) => <span key={i} style={{ background: '#fef3c7', color: '#b45309', padding: '1px 6px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: 500 }}>{tag}</span>)}
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                </tbody>
                                            </table>
                                        ) : (
                                            <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.9)' }}>
                                                {selectedUser.Email}
                                            </div>
                                        )}

                                        <div style={{ fontSize: '0.75rem', marginTop: '0.25rem', display: 'flex', gap: '0.75rem', color: 'rgba(255,255,255,0.8)' }}>
                                            {selectedUser.Profile && <span>Profile: {selectedUser.Profile}</span>}
                                            <span>Division: {selectedUser.Division || selectedUser.Attributes?.find(a => a.Name === 'custom:division')?.Value || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                                <button onClick={closeModal} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', color: 'white', padding: '0.4rem', borderRadius: '50%' }} title="Close">
                                    <X size={16} />
                                </button>
                            </div>

                            <div style={{ flex: 1, overflowY: 'auto', padding: '1rem', background: '#fafafa' }}>
                                {loadingDetails ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem', fontSize: '0.9rem' }}>Loading history...</div>
                                ) : detailsError ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem', fontSize: '0.9rem', color: 'red' }}>
                                        {detailsError.status === 403 ? "Access restricted to your division members only." : "Failed to load user history."}
                                    </div>
                                ) : userDetails ? (
                                    <div>
                                        {/* Summary Cards */}
                                        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
                                            <SummaryCard title="Tasks Created" value={(userDetails.summary?.openTasks || 0) + (userDetails.summary?.completedTasks || 0)} icon={<Briefcase size={16} />} color="#007bff" />
                                            <SummaryCard title="Tasks Completed" value={userDetails.summary?.completedTasks || 0} icon={<Briefcase size={16} />} color="#28a745" />
                                            <SummaryCard title="Events" value={(userDetails.summary?.openEvents || 0) + (userDetails.summary?.completedEvents || 0)} icon={<Calendar size={16} />} color="#17a2b8" />
                                        </div>

                                        {/* Recent Tasks List */}
                                        <div className="modern-card" style={{ padding: '0.75rem', marginBottom: '1rem', borderRadius: '8px', border: '1px solid #eef2f6' }}>
                                            <h4 style={{ marginTop: 0, fontSize: '0.95rem', marginBottom: '0.5rem' }}>Recent Tasks</h4>
                                            {userDetails.tasks?.length > 0 ? (
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                                    <thead>
                                                        <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                                                            <th style={{ padding: '0.35rem', textAlign: 'left', color: '#666' }}>Title</th>
                                                            <th style={{ padding: '0.35rem', textAlign: 'left', color: '#666' }}>Status</th>
                                                            <th style={{ padding: '0.35rem', textAlign: 'left', color: '#666' }}>Date</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {userDetails.tasks.slice(0, 10).map(task => (
                                                            <tr key={task.TaskID} style={{ borderBottom: '1px solid #f9f9f9' }}>
                                                                <td style={{ padding: '0.35rem' }}>{task.Title}</td>
                                                                <td style={{ padding: '0.35rem' }}>
                                                                    <span style={{
                                                                        padding: '0.1rem 0.4rem', borderRadius: '12px', fontSize: '0.7rem',
                                                                        background: task.Status === 'Completed' ? '#d4edda' : '#fff3cd',
                                                                        color: task.Status === 'Completed' ? '#155724' : '#856404'
                                                                    }}>
                                                                        {task.Status}
                                                                    </span>
                                                                </td>
                                                                <td style={{ padding: '0.35rem', color: '#888' }}>{task.CreatedAt?.split('T')[0]}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            ) : <p style={{ color: '#999', fontStyle: 'italic', fontSize: '0.85rem' }}>No tasks found.</p>}
                                        </div>

                                        {/* Recent Activity List */}
                                        <div className="modern-card" style={{ padding: '0.75rem', marginBottom: '1rem', borderRadius: '8px', border: '1px solid #eef2f6' }}>
                                            <h4 style={{ marginTop: 0, fontSize: '0.95rem', marginBottom: '0.5rem' }}>Recent Activity</h4>
                                            {(() => {
                                                const allActivity = [
                                                    ...(userDetails.tasks || []).map(t => ({ ...t, Type: 'Task', ID: t.TaskID })),
                                                    ...(userDetails.subtasks || []).map(t => ({ 
                                                        ...t, 
                                                        Type: 'Subtask', 
                                                        ID: t.SubTaskID,
                                                        Title: t.Title || t.ActivityNameManual || t.ActivityType || 'Untitled Subtask'
                                                    })),
                                                    ...(userDetails.events || []).map(e => ({ ...e, Type: 'Event', ID: e.EventID })),
                                                    ...(userDetails.subevents || []).map(e => ({ ...e, Type: 'Subevent', ID: e.SubEventID }))
                                                ].sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt)).slice(0, 20);

                                                if (allActivity.length === 0) return <p style={{ color: '#999', fontStyle: 'italic', fontSize: '0.85rem' }}>No recent activity found.</p>;

                                                return (
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                                        <thead>
                                                            <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                                                                <th style={{ padding: '0.35rem', textAlign: 'left', color: '#666' }}>Type</th>
                                                                <th style={{ padding: '0.35rem', textAlign: 'left', color: '#666' }}>Title</th>
                                                                <th style={{ padding: '0.35rem', textAlign: 'left', color: '#666' }}>Status</th>
                                                                <th style={{ padding: '0.35rem', textAlign: 'left', color: '#666' }}>Date</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {allActivity.map(item => (
                                                                <tr key={`${item.Type}-${item.ID}`} style={{ borderBottom: '1px solid #f9f9f9' }}>
                                                                    <td style={{ padding: '0.35rem' }}>
                                                                        <span style={{
                                                                            fontSize: '0.7rem', fontWeight: 600, padding: '1px 5px', borderRadius: '4px',
                                                                            background: item.Type === 'Task' ? '#e3f2fd' :
                                                                                item.Type === 'Event' ? '#f3e8ff' :
                                                                                    item.Type === 'Subtask' ? '#fff3e0' : '#f1f5f9',
                                                                            color: item.Type === 'Task' ? '#1565c0' :
                                                                                item.Type === 'Event' ? '#7c3aed' :
                                                                                    item.Type === 'Subtask' ? '#e65100' : '#475569'
                                                                        }}>
                                                                            {item.Type}
                                                                        </span>
                                                                    </td>
                                                                    <td style={{ padding: '0.35rem' }}>
                                                                        <div style={{ fontWeight: 500, color: '#333', fontSize: '0.85rem' }}>{item.Title || 'Untitled'}</div>
                                                                        {item.ParentTitle && (
                                                                            <div style={{ fontSize: '0.75rem', color: '#4b5563', marginTop: '0.15rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                                                                <span style={{ color: '#9ca3af', fontWeight: 500 }}>Part of:</span>
                                                                                <span style={{ fontWeight: 500, color: '#1f2937' }}>{item.ParentTitle}</span>
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td style={{ padding: '0.35rem' }}>
                                                                        <span style={{
                                                                            fontSize: '0.75rem', fontWeight: 600,
                                                                            color: item.Status === 'Completed' || item.Status === 'Scheduled' ? '#16a34a' :
                                                                                item.Status === 'Cancelled' ? '#dc2626' : '#d97706'
                                                                        }}>
                                                                            {item.Status || 'Open'}
                                                                        </span>
                                                                    </td>
                                                                    <td style={{ padding: '0.35rem', color: '#888' }}>{item.CreatedAt?.split('T')[0]}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </>
                    ) : (
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                            <div style={{ padding: '1rem', background: '#f8f9fa', borderRadius: '50%', marginBottom: '0.5rem' }}>
                                <User size={32} color="#dee2e6" />
                            </div>
                            <h3 style={{ fontSize: '1.1rem', margin: '0.5rem 0' }}>Select a Team Member</h3>
                            <p style={{ fontSize: '0.85rem' }}>Click on a team member from the list to view their history.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const SummaryCard = ({ title, value, icon, color }) => (
    <div className="modern-card" style={{
        flex: 1, minWidth: '120px', display: 'flex', alignItems: 'center', gap: '0.75rem',
        background: 'white', border: 'none', borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)', padding: '1rem'
    }}>
        <div style={{ padding: '0.5rem', borderRadius: '50%', background: `${color}15`, color: color }}>
            {icon}
        </div>
        <div>
            <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.1rem', fontWeight: 500 }}>{title}</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 600, color: '#202124' }}>{value}</div>
        </div>
    </div>
);

export default CoordinatorTeams;
